import React, { useEffect, useRef } from 'react';
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    isRouteErrorResponse,
    useLocation,
    useRouteError,
    useRouteLoaderData,
} from 'react-router';
import { useSetAtom } from 'jotai';
import type { LinksFunction } from 'react-router';
import type { GrowthBookSSRData } from '@growthbook/growthbook-react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { useHydrated } from 'remix-utils';
import {
    getThemeByUrl,
    processTheme,
    useFeature,
    useMatchesHandleData,
    useRootData,
} from '@modules/root';
import {
    DeviceBrowserContextProvider,
    DeviceTypeContextProvider,
    AppDisplayModeContextProvider,
    Provider,
    baseTheme,
    RendererProvider,
} from '@archipro-design/aria';
import { BodyPixels, HeadPixels } from '@modules/tracking';
import { ClientAdvertisingPixels } from '@modules/tracking/component/ClientAdvertisingPixels';
import { UsNoticeBanner } from '@modules/consent/UsNoticeBanner';
import ErrorPage from '../../error/page/ErrorPage';
import type { ReactNode, JSX } from 'react';
import { ConfigProvider } from '@archipro-website/config/bindings/react';
import { CountryProvider } from '@archipro-website/localisation';
import CustomScrollRestoration from '@modules/root/component/scroll-restoration/CustomScrollRestoration';
import { LocalisationContextProvider } from '@archipro-website/localisation';
import { useMockDevice } from '@modules/root/hook/use-mock-device';
import { DevicePreview } from '@modules/root/component/device-preview/DevicePreview';
import { TrackerContext, useArchiproTracking } from '@archipro-website/tracker';
import { useGrowthBookClient } from '../hook/use-growthbook';
import Debug from '~/modules/error/component/debug/Debug';
import { createFelaRendererFactory } from '../../../../react-northstar-fela-renderer/src/createFelaRenderer';
import { useFluentContext } from '@fluentui/react-northstar';
import { _displayModeAtom } from '@rocco/states/uiContextAtomDisplayMode';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { getSiteLang } from '~/utils/countryHelper';
import type { RootData } from '~/root';
import { ApolloClientProvider } from '../context/ApolloClientProvider';

/**
 * Site wide fonts and favicons.
 */
export const links: LinksFunction = () => {
    return [
        // Preconnect hints for TypeKit fonts to reduce latency
        // Note: crossOrigin removed for stylesheet preconnect (CSS doesn't use CORS)
        {
            rel: 'preconnect',
            href: 'https://use.typekit.net',
        },
        {
            rel: 'preconnect',
            href: 'https://p.typekit.net',
            crossOrigin: 'anonymous',
        },
        {
            rel: 'dns-prefetch',
            href: 'https://use.typekit.net',
        },
        // Adobe Fonts kit supplying neue-haas-grotesk-display / -text. Declared here
        // so it renders into <head> and the preload scanner starts it on the first
        // pass. The font binaries sit behind rotating hashed p.typekit.net URLs and
        // cannot be preloaded, so an early kit request is the only latency lever.
        {
            rel: 'stylesheet',
            href: 'https://use.typekit.net/hpu6ldu.css',
        },
        // Commenting this out again, its still causing mobile performance
        // issues on mobile. Should fall back to pngs below.
        // {
        //     rel: 'icon',
        //     type: 'image/svg+xml',
        //     href: '/assets/website/ui/favicon-global-ninjas/favicon-new.svg',
        // },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '196x196',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-196x196.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '16x16',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-16x16.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '32x32',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-32x32.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '96x96',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-96x96.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '128x128',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-128x128.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '512x512',
            href: '/assets/website/ui/favicon-global-ninjas/android-chrome-black-512x512.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '57x57',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-57x57.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '114x114',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-114x114.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '60x60',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-60x60.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '72x72',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-72x72.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '76x76',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-76x76.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '120x120',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-120x120.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '152x152',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-152x152.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '144x144',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-144x144.png',
        },
    ];
};

/**
 * Full body of the app rendered on the server and the browser.
 */
const Body = ({ children }: { children: React.ReactNode }) => {
    const {
        device,
        localisation,
        config,
        appDisplayMode,
        user,
        growthbookContext,
        env,
    } = useRootData();

    const location = useLocation();
    const hydrated = useHydrated();
    const mockDevice = useMockDevice(location.search);

    // set the display mode for using in rocco UI components
    const setDisplayMode = useSetAtom(_displayModeAtom);
    setDisplayMode(appDisplayMode);

    // Gate the listing→detail view transitions (BC-4786) for rocco tiles and
    // the detail overlay. The dev URL override (?transitions=1) latches in
    // sessionStorage so it survives both the navigations it enables AND full
    // document reloads (chunk-recovery reloads on dev envs silently killed an
    // in-memory latch, which read as transitions randomly disappearing).
    const viewTransitionsFlag = useFeature('details_transition_2026');
    const viewTransitionsDevLatch = useRef(false);
    if (import.meta.env.DEV && typeof window !== 'undefined') {
        if (new URLSearchParams(location.search).get('transitions') === '1') {
            viewTransitionsDevLatch.current = true;
            window.sessionStorage.setItem('ap-transitions-dev', '1');
        } else if (
            window.sessionStorage.getItem('ap-transitions-dev') === '1'
        ) {
            viewTransitionsDevLatch.current = true;
        }
    }
    const viewTransitionsEnabled =
        viewTransitionsFlag || viewTransitionsDevLatch.current;
    const setViewTransitionsEnabled = useSetAtom(
        uiContextAtomViewTransitionsEnabled,
    );
    setViewTransitionsEnabled(viewTransitionsEnabled);
    // The CSS kill-switch (tailwind.css) reads this off the root element:
    // React Router persists a navigation's view-transition opt-in per session
    // and re-applies it on back/forward, so a traversal can still start a
    // transition after the flag turns off — without the attribute, that
    // transition collapses to an instant swap instead of cross-fading.
    useEffect(() => {
        document.documentElement.toggleAttribute(
            'data-view-transitions',
            viewTransitionsEnabled,
        );
    }, [viewTransitionsEnabled]);

    if (mockDevice) {
        device.type = mockDevice;
    }

    const theme = processTheme(
        getThemeByUrl(location.pathname),
        device.type === 'desktop',
    );

    // Sets up all of our different tracking pixels
    const tracker = useArchiproTracking(location, user, {
        ga4TrackingId: config.pixels.ga4TrackingId,
        googleAdsConversionLabels: config.pixels.googleAdsConversionLabels,
    });

    const gb = useGrowthBookClient(
        growthbookContext as GrowthBookSSRData,
        tracker,
    );

    // only load the dynamic favicon script in development environment
    const isDevEnv =
        env.stage !== 'Production' ||
        env.host === 'localhost' ||
        env.host.includes('.team.archipro.');
    const dynamicFaviconScript = isDevEnv ? (
        <script
            src={`/_resources/app/js/dynamic-favicon.js?VERSION=${env.version}&date=${new Date().toISOString().split('T')[0]}`}
        />
    ) : null;

    return (
        <div
            id="root"
            style={{
                backgroundColor: theme.siteVariables.bodyBackground,
            }}
        >
            {mockDevice === undefined && <BodyPixels {...config.pixels} />}

            <TrackerContext.Provider value={tracker}>
                <GrowthBookProvider growthbook={gb}>
                    <Provider theme={theme}>
                        {import.meta.env.DEV ? <Debug /> : null}
                        <LocalisationContextProvider
                            data={localisation}
                            hydrated={hydrated}
                        >
                            <DeviceTypeContextProvider device={device.type}>
                                <DeviceBrowserContextProvider
                                    browser={device.browser}
                                >
                                    <AppDisplayModeContextProvider
                                        mode={mockDevice ?? appDisplayMode}
                                    >
                                        <CountryProvider
                                            code={localisation.country}
                                        >
                                            <ConfigProvider config={config}>
                                                <ApolloClientProvider>
                                                    <ClientAdvertisingPixels
                                                        {...config.pixels}
                                                        enabled={
                                                            mockDevice ===
                                                            undefined
                                                        }
                                                    />
                                                    <UsNoticeBanner />
                                                    {mockDevice ? (
                                                        <DevicePreview>
                                                            {children}
                                                        </DevicePreview>
                                                    ) : (
                                                        <>{children}</>
                                                    )}
                                                </ApolloClientProvider>
                                            </ConfigProvider>
                                        </CountryProvider>
                                    </AppDisplayModeContextProvider>
                                </DeviceBrowserContextProvider>
                            </DeviceTypeContextProvider>
                        </LocalisationContextProvider>
                    </Provider>
                </GrowthBookProvider>
            </TrackerContext.Provider>
            <CustomScrollRestoration />
            <Scripts />
            {dynamicFaviconScript}
        </div>
    );
};

/**
 * Full head
 */
const Head = () => {
    const { device } = useRootData();
    const disableMobileUserScaling = useMatchesHandleData<boolean>(
        'disableMobileUserScaling',
        false,
    );

    const noUserScalable =
        device.type !== 'desktop' && disableMobileUserScaling;
    const viewport = noUserScalable
        ? 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no, shrink-to-fit=no'
        : device?.os.isApple
          ? 'width=device-width, initial-scale=1.0, maximum-scale=2, viewport-fit=cover'
          : 'width=device-width,initial-scale=1';

    return (
        <>
            <meta charSet="utf-8" />
            <meta name="viewport" content={viewport} />
            {/* Progressive enhancement for scroll-reveal content (product grids,
                carousels): it SSRs at opacity-0 and is revealed by a client
                IntersectionObserver. Without JS the observer never fires, so
                force it visible for no-JS agents and crawlers. JS users keep the
                reveal animation untouched. */}
            <noscript>
                <style
                    dangerouslySetInnerHTML={{
                        __html: '.scroll-reveal{opacity:1!important;transform:none!important}',
                    }}
                />
            </noscript>
            {noUserScalable && (
                <meta name="HandheldFriendly" content="true"></meta>
            )}
            <meta name="application-name" content="&nbsp;" />
            <meta name="msapplication-TileColor" content="#FFFFFF" />
            <meta
                name="msapplication-TileImage"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-144x144.png"
            />
            <meta
                name="msapplication-square70x70logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-70x70.png"
            />
            <meta
                name="msapplication-square150x150logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-150x150.png"
            />
            <meta
                name="msapplication-wide310x150logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-310x150.png"
            />
            <meta
                name="msapplication-square310x310logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-310x310.png"
            />
            <Meta />
            <Links />
        </>
    );
};

/**
 * This is version of the head that `should` not throw errors.
 */
const SafeHead = () => {
    return (
        <>
            <meta charSet="utf-8" />
            <meta
                name="viewport"
                content="width=device-width,initial-scale=1"
            />
            <Links />
        </>
    );
};

const RootPage = () => {
    const { config, localisation } = useRootData();
    const location = useLocation();
    const mockDevice = useMockDevice(location.search);
    const lang = getSiteLang(localisation.country);

    return (
        <html lang={lang}>
            <head suppressHydrationWarning>
                <Head />
                {mockDevice === undefined && <HeadPixels {...config.pixels} />}
            </head>
            <body suppressHydrationWarning>
                <Body>
                    <Outlet />
                </Body>
            </body>
        </html>
    );
};

export const ErrorBoundary = () => {
    const error = useRouteError();
    const rootData = useRouteLoaderData('root') as RootData | undefined;

    const fluentUI = useFluentContext();
    // Detect a missing renderer (root loader failed before the renderer was
    // attached) and re-attach a fresh Fela renderer for the error tree.
    const isNoopProvider = fluentUI.renderer.Provider.name === 'NoopProvider';
    const { current: createFelaRenderer } = useRef(
        createFelaRendererFactory().create,
    );
    const renderWithRenderer = (element: JSX.Element) => {
        if (!isNoopProvider) return element;
        return (
            <RendererProvider value={createFelaRenderer}>
                <Provider theme={baseTheme}>{element}</Provider>
            </RendererProvider>
        );
    };

    let errorObj: Error = new Error('Unknown error');
    if (isRouteErrorResponse(error)) {
        const data = error.data;
        const msg =
            typeof data === 'string'
                ? data
                : ((data as { message?: string } | null)?.message ??
                  `${error.status}`);
        errorObj = new Error(`${error.status}: ${msg}`);
    } else if (error instanceof Error) {
        errorObj = error;
    }

    const lang = rootData?.localisation?.country
        ? getSiteLang(rootData.localisation.country)
        : 'en';
    const errorPage = <ErrorPage error={errorObj} />;
    const bodyContent = rootData
        ? renderWithRenderer(<Body>{errorPage}</Body>)
        : // No root data means `<Body>` (which renders `<Scripts />`) is skipped;
          // include `<Scripts />` explicitly so the client can still hydrate and
          // recover instead of being stuck on a script-less static error page.
          renderWithRenderer(
              <>
                  {errorPage}
                  <Scripts />
              </>,
          );

    return (
        <html lang={lang}>
            <head suppressHydrationWarning>
                <SafeHead />
            </head>
            <body suppressHydrationWarning>{bodyContent}</body>
        </html>
    );
};

export default RootPage;
